<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Por Canal',
  },
});

// Calculate total
const total = computed(() => {
  return props.data.reduce((sum, item) => sum + (item.value || 0), 0);
});

// Generate SVG arc path
function describeArc(cx, cy, radius, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return [
    'M',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
  ].join(' ');
}

function polarToCartesian(cx, cy, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  };
}

// Generate arcs for donut chart
const arcs = computed(() => {
  if (total.value === 0) return [];

  const cx = 100;
  const cy = 100;
  const radius = 70;
  let currentAngle = 0;

  return props.data.map(item => {
    const percentage = (item.value / total.value) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    return {
      ...item,
      percentage: percentage.toFixed(1),
      path: describeArc(
        cx,
        cy,
        radius,
        startAngle,
        Math.min(endAngle, startAngle + 359.9)
      ),
    };
  });
});
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <!-- Header -->
    <div class="mb-4">
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="total === 0"
      class="flex items-center justify-center h-64 text-n-slate-11"
    >
      <span class="text-sm">Sem dados disponíveis</span>
    </div>

    <!-- Chart -->
    <div v-else class="flex flex-col items-center">
      <!-- Donut Chart -->
      <div class="w-52 h-52">
        <svg viewBox="0 0 200 200" class="w-full h-full">
          <!-- Background circle -->
          <circle
            cx="100"
            cy="100"
            r="70"
            fill="none"
            stroke="currentColor"
            stroke-opacity="0.1"
            stroke-width="30"
          />

          <!-- Data arcs -->
          <path
            v-for="(arc, index) in arcs"
            :key="index"
            :d="arc.path"
            fill="none"
            :stroke="arc.color"
            stroke-width="30"
            stroke-linecap="butt"
          />
        </svg>
      </div>

      <!-- Legend -->
      <div class="flex flex-wrap items-center justify-center gap-4 mt-4">
        <div
          v-for="(item, index) in data"
          :key="index"
          class="flex items-center gap-2"
        >
          <span
            class="w-3 h-3 rounded-full"
            :style="{ backgroundColor: item.color }"
          />
          <span class="text-xs text-n-slate-11">
            {{ item.label }} ({{ item.value }})
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
